import { useMemo } from "react";
import { HeroConfig } from "./CompassModal";
import type {
  CompassModalConfig,
  UseCompassModalParams,
  UseCompassModalReturn,
  Resolvable,
} from "../../../types/modalTypes";

export type {
  CompassModalConfig,
  ModalStateConfig,
  StateHeroConfig,
  UseCompassModalParams,
  UseCompassModalReturn,
  Resolvable,
} from "../../../types/modalTypes";

const resolve = <T, C>(
  val: Resolvable<T, C> | undefined,
  ctx: C,
): T | undefined =>
  val === undefined
    ? undefined
    : typeof val === "function"
      ? (val as (c: C) => T)(ctx)
      : val;

export function useCompassModal<TState extends string, TContext = unknown>({
  config,
  state,
  context,
}: UseCompassModalParams<TState, TContext>): UseCompassModalReturn {
  const ctx = (context ?? {}) as TContext;

  return useMemo(() => {
    const stateConfig = config.states[state];
    const h = stateConfig?.hero;

    const hero: HeroConfig | undefined = h
      ? {
          kind: h.kind,
          source: h.source,
          animation: h.animation,
          alt: h.alt,
          title: resolve(h.title, ctx),
          description: resolve(h.description, ctx),
          suffix: resolve(h.suffix, ctx),
          size: h.size,
          padding: h.padding,
          offsetX: h.offsetX,
          offsetY: h.offsetY,
          isProcessing: stateConfig?.isProcessing,
          imageBorderRadius: h.imageBorderRadius,
          imageOpacity: h.imageOpacity,
          backgroundColor: h.backgroundColor,
          titleColor: h.titleColor,
          accentColor: h.accentColor,
        }
      : undefined;

    return {
      modalProps: {
        title: resolve(config.title, ctx) ?? "",
        titleIcon: config.titleIcon,
        titleIconSize: config.titleIconSize,
        modalSize: config.modalSize,
        size: config.size,
        hero,
      },
      isProcessing: stateConfig?.isProcessing ?? false,
      currentState: state,
    };
  }, [config, state, ctx]);
}

export const defineModalConfig = <TState extends string, TContext = unknown>(
  config: CompassModalConfig<TState, TContext>,
): CompassModalConfig<TState, TContext> => config;
